import { Link, createFileRoute, redirect } from '@tanstack/react-router';

// The FAQ now lives as a section of the combined /rules page (Rules & FAQ).
// Keep this URL working for old links and bookmarks by redirecting to it.
export const Route = createFileRoute('/faq')({
  beforeLoad: () => {
    throw redirect({ to: '/rules', hash: 'faq' });
  },
});

export const FAQ_JSONLD = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'What is Prod Battle?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Prod Battle is a real-time online beat-battle platform. Producers join a match, get the same sample kit, make a beat under a timer, and the audience votes blind. Skill, not gear.',
      },
    },
    {
      '@type': 'Question',
      name: 'How does a producer battle work?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Each match has four phases: lobby, build (a timed window where everyone produces with the same kit), submit, and vote. Voting is blind. Winners earn ranked points in their genre.',
      },
    },
    {
      '@type': 'Question',
      name: 'Is Prod Battle free?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. Free accounts get unlimited matches and uploads. Guests get 3 matches per day. The supporter tier unlocks monthly playlists, early genre access, and other perks.',
      },
    },
    {
      '@type': 'Question',
      name: 'Which genres can I battle in?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Trap, house, drum and bass, hip-hop, lo-fi, phonk, and more. New genres are proposed by the community and voted in.',
      },
    },
    {
      '@type': 'Question',
      name: 'How are battles judged?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Blind audience voting on a 1 to 5 scale per submission. Producer identities are hidden until results. Winners climb the genre leaderboard via an ELO-style rating.',
      },
    },
    {
      '@type': 'Question',
      name: 'Do I need a DAW to play?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. Use any DAW you like (FL Studio, Ableton, Logic, free options like Cakewalk or BandLab). You produce locally and upload your beat through the browser.',
      },
    },
    {
      '@type': 'Question',
      name: 'What is the difference between a weekly showcase and a bracket tournament?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Weekly showcases are upload-once events: producers submit one track and the community votes for 7 days. The highest vote total wins with no bracket. Bracket tournaments start with a showcase window, then run single-elimination 1v1 matches. The showcase track seeds round 1 so producers do not upload twice; winners make a fresh beat from round 2 onward.',
      },
    },
    {
      '@type': 'Question',
      name: 'What happens if I submit but skip the vote phase?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'The match still completes; we tally whatever votes were cast. Producers who submit and then ghost the vote phase take a small honor penalty (-1 to -3 depending on mode). First-offence forgiveness halves it.',
      },
    },
    {
      '@type': 'Question',
      name: 'How long do I have to make a beat?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Quickplay matches give you 10 minutes; Ranked matches give 20 minutes. Tournament rounds use 10, 30, or 60 minutes depending on the bracket. Private rooms let the host pick from 1 to 60 minutes. Daily Challenge is async with a 24-hour window.',
      },
    },
    {
      '@type': 'Question',
      name: 'When does the match auto-start?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Quickplay, Ranked, and Sample Flip lobbies auto-start 90 seconds after the third producer is seated. The room fires immediately if it fills up or every seated producer hits Ready. Private rooms are host-start.',
      },
    },
    {
      '@type': 'Question',
      name: 'Can I vote for my own track?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'No. Your own submission is shown with disabled stars and a "your track" badge in the vote screen. The API also silently drops any self-vote attempts as a server-side defense.',
      },
    },
    {
      '@type': 'Question',
      name: 'What is A&R on Prod Battle?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'A&R is the label-scouting layer. Verified A&R users pick standout tracks, co-sign producers, post briefs, and reach out to producers they want to work with. Picks drive the Most Scouted ranking and an "A&R\'s Choice" badge in the feed. The whole A&R layer is for registered users only.',
      },
    },
    {
      '@type': 'Question',
      name: 'How do friends and the inbox work?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Add a friend from their profile - it sends a request the other producer must accept before you are connected. Friend requests and A&R contact requests both land in your inbox, where you accept, decline, or remove connections. You can show or hide your friends on your public profile from settings.',
      },
    },
    {
      '@type': 'Question',
      name: 'Can I control which emails Prod Battle sends me?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. Settings lets you opt in or out of each email category: tournament activity, daily challenge, match results, honor alerts, A&R interest, and social. Only account security and billing emails are always on. In-app inbox requests still appear even if you turn off their email.',
      },
    },
  ],
};

// Collapsible Q&A using <details>/<summary>. Native, accessible, no JS, and
// every question gets a stable URL fragment via id={...} so we can link to
// individual answers from other pages or support replies.
function Q({
  id,
  q,
  children,
}: {
  id: string;
  q: string;
  children: React.ReactNode;
}) {
  return (
    <details
      id={id}
      className="group rounded-md border border-border bg-card open:bg-muted/40 transition-colors"
    >
      <summary className="cursor-pointer list-none px-4 py-3 text-sm font-medium flex items-center justify-between">
        <span>{q}</span>
        <span className="text-muted-foreground transition-transform group-open:rotate-90">›</span>
      </summary>
      <div className="px-4 pb-4 pt-1 text-sm text-muted-foreground space-y-2 leading-relaxed">
        {children}
      </div>
    </details>
  );
}

function Section({
  id,
  title,
  blurb,
  children,
}: {
  id: string;
  title: string;
  blurb?: string;
  children: React.ReactNode;
}) {
  return (
    <section id={id} className="space-y-3 scroll-mt-24">
      <header>
        <h2 className="text-xl font-semibold text-foreground">{title}</h2>
        {blurb && <p className="text-sm text-muted-foreground">{blurb}</p>}
      </header>
      <div className="space-y-2">{children}</div>
    </section>
  );
}

export function FaqBody() {
  const sections = [
    { id: 'basics', label: 'Basics' },
    { id: 'modes', label: 'Game modes' },
    { id: 'packs', label: 'Sample packs' },
    { id: 'ranked', label: 'Ranked & tiers' },
    { id: 'honor', label: 'Honor & ethics' },
    { id: 'voting', label: 'Voting' },
    { id: 'achievements', label: 'Achievements' },
    { id: 'ar', label: 'A&R & scouting' },
    { id: 'social', label: 'Friends & inbox' },
    { id: 'profile', label: 'Profile' },
    { id: 'notifications', label: 'Notifications' },
    { id: 'genres', label: 'Genres' },
    { id: 'premium', label: 'Supporter' },
    { id: 'safety', label: 'Trust & safety' },
    { id: 'help', label: 'Troubleshooting' },
    { id: 'roadmap', label: 'Roadmap' },
  ];

  return (
    <div className="space-y-10">
      <header className="space-y-3">
        <p className="text-muted-foreground">
          Everything about how Prod Battle works today. The platform is in pre-alpha; only what's
          actually built is documented here. The{' '}
          <a href="#roadmap" className="underline">
            Roadmap
          </a>{' '}
          section at the bottom lists what's still in flight.
        </p>
        <nav className="flex flex-wrap gap-2 pt-2 text-xs">
          {sections.map((s) => (
            <a
              key={s.id}
              href={`#${s.id}`}
              className="rounded-full border border-border px-3 py-1 text-muted-foreground hover:text-foreground hover:border-primary"
            >
              {s.label}
            </a>
          ))}
        </nav>
      </header>

      {/* ─── Basics ──────────────────────────────────────────────────────── */}
      <Section id="basics" title="Basics" blurb="What this is and how to start.">
        <Q id="q-what-is" q="What is Prod Battle?">
          A multiplayer game for music producers. You and other producers get the same sample pack,
          a clock, and a DAW of your choice. When the timer ends, everyone uploads what they made
          and the room scores each entry 1-5. Different modes, same idea: one shared kit, many
          flips, one winner.
        </Q>
        <Q id="q-account-needed" q="Do I need an account?">
          No, you can play 3 free Quickplay matches a day as a guest. Sign up (free) to remove the
          daily cap, plus get ranked play, voting, pack uploads, achievements, honor, and a profile.
          Sign-up is via email, Google, or Discord.
        </Q>
        <Q id="q-cost" q="Is it free?">
          Yes, the platform is free to use. Supporter (€2,95/mo) is a recognition tier - cosmetics,
          storage, and small extras like a monthly curated playlist (Discord-role sync coming soon).
          NO competitive advantage: votes weigh the same, matchmaking is the same, no priority
          queue. See the Supporter section.
        </Q>
        <Q id="q-daw" q="What DAW do I need?">
          Any DAW that exports WAV / MP3 / OGG. Prod Battle doesn't run inside your DAW - we hand
          you a sample pack, you flip it however you like, and you upload the finished render.
        </Q>
        <Q id="q-formats" q="What audio formats and sizes can I upload?">
          WAV, MP3, or OGG. Single 20 MB cap per submission. Submissions over the deadline are
          rejected.
        </Q>
        <Q id="q-skill" q="Do I need to be a 'good' producer?">
          No. Quickplay has no skill gating - jump in. Ranked uses a per-genre rating that adjusts
          after each match, with a 10-match calibration window so you find your tier before LP and
          rank are shown.
        </Q>
        <Q id="q-lobbies-browser" q="Where do I see open matches I can join?">
          The{' '}
          <Link to="/lobbies" className="underline">
            /lobbies
          </Link>{' '}
          page lists every open public match: quickplay, ranked, sample flip, the daily challenge,
          and any private rooms whose host opted in to "list publicly". Each row shows the genre,
          player count, host (if any), and the auto-start countdown if one is running.
        </Q>
        <Q id="q-lobby-autostart" q="Why does the lobby start automatically?">
          For Quickplay, Ranked, and Sample Flip, the server fires a 90-second countdown the moment
          3 producers are seated. When it hits zero (or the room fills, or every seated producer
          hits Ready) the match starts. This keeps lobbies from sitting empty and punishes nobody -
          late joiners are welcome until the timer fires. Private rooms are host-start; tournaments
          use their scheduled start time.
        </Q>
        <Q id="q-feed-winners" q="Why does the Discover feed only show winning tracks?">
          The{' '}
          <Link to="/feed" className="underline">
            /feed
          </Link>{' '}
          surfaces match winners (final rank #1) so the discovery signal stays high. Every other
          submission still lives on the producer's profile - just not in the global feed.
        </Q>
      </Section>

      {/* ─── Modes ───────────────────────────────────────────────────────── */}
      <Section id="modes" title="Game modes" blurb="Seven ways to battle on a shared kit.">
        <Q id="q-mode-quickplay" q="Quickplay">
          The casual entry mode. Up to 8 producers, random genre, the server picks the pack.{' '}
          <strong>10-minute submit window</strong>. Anonymous guests get 3 matches a day. Signing up
          is free and removes the cap. No rating impact - just play.
        </Q>
        <Q id="q-mode-ranked" q="Ranked">
          You pick the genre, the server picks the pack at random. Up to 8 producers, FFA.{' '}
          <strong>20-minute submit window</strong> (more deliberate than Quickplay). Wins and losses
          adjust your per-genre rating, which maps to a tier (Bronze ... Grandmaster with III/II/I
          sub-divisions). The pack is randomised so nobody can stack their best stems.
        </Q>
        <Q id="q-mode-private" q="Private rooms">
          Make a room with a code, share with friends. 1v1 to 5v5 teams supported. You CAN pick a
          sample pack here (any pool pack, or your own uploaded packs). No rating change - it's for
          fun and training.
        </Q>
        <Q id="q-mode-daily" q="Daily Challenge">
          One pack, one prompt, runs UTC midnight to UTC midnight. Async - submit any time during
          the window. Up to 20 submitters per day. Submissions must be{' '}
          <strong>90 seconds to 4 minutes</strong>; voters hear a 60-second preview of each track.
          Voting on at least one entry is enough to keep your honor (vs. all-other-entries required
          in live battle modes - daily is friendlier for casual listeners). Voting stays open
          indefinitely after the day ends, so you can still cast scores on past challenges.
          Supporter-only access.
        </Q>
        <Q id="q-mode-flip" q="Sample Flip">
          One audio loop (a vocal chop, a melody hook), everyone flips it their way. Different from
          regular battles because there's no kit - just one source you're allowed (and required) to
          transform.
        </Q>
        <Q id="q-mode-tournament" q="Tournaments">
          Two formats, one registration flow. Browse upcoming + recent at{' '}
          <Link to="/tournaments" className="underline">
            /tournaments
          </Link>
          . Honor 70+ required to enter.
          <br />
          <strong>Weekly showcase</strong> - auto-created every week (Sunday 12:00 UTC, random
          genre, 32 entrant slots). One upload, one 7-day vote window, top score wins. No bracket.
          Producer with the most votes earns the "crowd_favorite" and "tournament_winner"
          achievements. Simple.
          <br />
          <strong>Bracket tournaments</strong> - admin-scheduled, single elimination. Starts with a
          showcase window, then the bracket runs. See the next two questions.
        </Q>
        <Q
          id="q-mode-tournament-weekly-vs-bracket"
          q="Weekly showcase vs bracket tournament - what's different?"
        >
          <strong>Weekly showcase:</strong> upload once, collect votes for 7 days, highest vote
          total wins. No 1v1 matches, no rounds, no fresh uploads to advance. One track per
          producer, ever.
          <br />
          <strong>Bracket tournament:</strong> starts with a 3-day showcase window (same upload, but
          it seeds round 1 - see below), then single-elimination 1v1 matches. Winners make a new
          beat each round from round 2 onward. The showcase upload is reused for round 1 so you
          don't produce twice in a row.
        </Q>
        <Q id="q-mode-tournament-showcase" q="What's the showcase phase?">
          Between registration closing and the battle starting, every entrant uploads one showcase
          track. The whole signed-in community can listen and score the entries (1-5 scale). The
          top-scored entrant earns the "crowd_favorite" achievement and an honor bonus. Voting is
          gated to signed-in users only, entrants can't vote on their own track, and the same honor
          weight curve applies as in regular matches.
          <br />
          For weekly showcases, the showcase IS the tournament - no bracket follows. For bracket
          tournaments, the showcase tracks are carried directly into round-1 matches (producers
          don't upload a second time for round 1). Winners make a fresh beat each subsequent round.
        </Q>
        <Q
          id="q-mode-tournament-reminders"
          q="Do I get a reminder email before my tournament starts?"
        >
          Not today. We send transactional mail (sign-up verification, password reset) but no
          tournament reminders yet. On the roadmap. For now, check{' '}
          <Link to="/tournaments" className="underline">
            /tournaments
          </Link>{' '}
          for the countdown.
        </Q>
        <Q id="q-mode-practice" q="Practice mode">
          Solo. Pick a pack, flip it, no opponents, no rating, no deadlines. For warming up before
          ranked or learning a new genre.
        </Q>
      </Section>

      {/* ─── Packs ───────────────────────────────────────────────────────── */}
      <Section id="packs" title="Sample packs" blurb="The shared kit at the heart of every match.">
        <Q id="q-pack-what" q="What is a sample pack?">
          A bundle of stems (kick, snare, hihat, fx, vocal, etc.) for a specific genre. Every
          producer in a match gets the SAME pack so the comparison is fair.
        </Q>
        <Q id="q-pack-pool-vs-uploaded" q="Pool packs vs uploaded packs?">
          <strong>Pool packs</strong> are admin/A&R-curated and available to everyone - used in
          Quickplay, Ranked, Private rooms, Tournaments, and the Daily Challenge. (Sample Flip is
          the exception: it uses a single audio loop, not a kit.) <strong>Uploaded packs</strong>{' '}
          are user-made and only the uploader can use them in private rooms - until A&R promotes
          them to the pool, at which point any player can use them.
        </Q>
        <Q id="q-pack-pick" q="Can I pick the pack in Ranked?">
          No. Ranked always randomises the pack. It would be too easy to grind your favourite kit
          otherwise. Private rooms let you pick.
        </Q>
        <Q id="q-pack-recent" q="Why does the random pick favour newer packs?">
          As the catalogue grows, the server picks from the 5 most-recently-added pool packs in your
          chosen genre, and prefers ones you haven't played before. Keeps battles fresh and surfaces
          new content as it's added.
        </Q>
        <Q id="q-pack-upload" q="Can I upload my own pack?">
          Yes if you're signed up. Hit "Upload pack", attach your stems, attest the rights, and the
          pack is available in your private rooms immediately. A&R reviews it - if they promote it
          to the pool, every player on the platform can use it.
        </Q>
        <Q id="q-pack-rights" q="What if my pack contains copyrighted samples?">
          Don't upload material you don't own or have a clearance for. The upload form requires a
          rights attestation. If we receive a takedown, the pack is removed and the uploader is
          notified.
        </Q>
      </Section>

      {/* ─── Ranked & tiers ──────────────────────────────────────────────── */}
      <Section id="ranked" title="Ranked & tiers" blurb="LP, divisions, calibration, seasons.">
        <Q id="q-rating-system" q="How does ranking work?">
          Ranked tracks a per-genre rating per season. Win a match and your rating goes up, lose and
          it goes down. The amount depends on the LP gap between you and your opponents: beating
          someone higher than you gives more LP, losing to someone lower costs more. Pair updates
          use a K-factor of 24 (48 during calibration) and a per-rank LP clamp so a top player isn't
          punished for an off-peak match against a low rank.
        </Q>
        <Q id="q-rating-tiers" q="What are the tiers?">
          Bronze, Silver, Gold, Platinum, Diamond, Master, Grandmaster. All except Grandmaster split
          into III / II / I sub-divisions, so 19 ranks total. Tier boundaries:{' '}
          <span className="font-mono text-xs">
            Bronze 0-99, Silver 100-249, Gold 250-499, Platinum 500-999, Diamond 1000-1999, Master
            2000-3499, Grandmaster 3500+
          </span>
          . Boundaries + sub-division count are admin-tunable via the rules editor.
        </Q>
        <Q id="q-rating-start" q="What's calibration?">
          Your first 10 ranked matches are calibration. Your starting rating is 1500, and during
          calibration the system uses a wider LP swing (K=48) so it can find your skill level fast.
          While you're still calibrating, your tier and LP are hidden on your profile - we don't
          want you fixating on a half-formed estimate.
        </Q>
        <Q id="q-rating-genres" q="Why is rating per-genre?">
          Producing Hardstyle and Phonk are different skills. Ranking each genre separately means a
          Hardstyle specialist isn't punished for trying UK Garage and vice versa.
        </Q>
        <Q id="q-rating-leaderboard" q="What's on the leaderboard?">
          The top 100 of each genre per season. Top 10 = gold tier, 11-50 = silver, 51-100 = bronze.
        </Q>
        <Q id="q-rating-season" q="What's a season?">
          A defined time window for ranked play. Each season has its own per-genre standings.
          Seasons are pre-created on the server (we have a year of quarters seeded) so a season
          rollover never needs operator intervention. End-of-season soft reset (LP drops to ~60% of
          previous, floored at the prior tier's entry) is on the{' '}
          <a href="#roadmap" className="underline">
            Roadmap
          </a>{' '}
          - the math is in the rules editor; the cron isn't armed yet.
        </Q>
        <Q id="q-rating-lp-shown" q="Can I see my current LP and tier?">
          Yes - on your profile (`/@yourhandle`) and on `/me`. The number and tier label are hidden
          during the 10-match calibration window.
        </Q>
      </Section>

      {/* ─── Honor ──────────────────────────────────────────────────────── */}
      <Section
        id="honor"
        title="Honor & ethics"
        blurb="The number that keeps things civil across all modes."
      >
        <Q id="q-honor-what" q="What is honor?">
          A score from 0 to 100 attached to your account. Everyone starts at 100. Bad behaviour
          (abandons, empty submissions) drops it. Clean play brings it back. On public profiles
          honor is rendered as 0-5 yellow stars (1 star per 20 honor); on your own /me you see the
          exact number.
        </Q>
        <Q id="q-honor-lose" q="What loses honor?">
          <p className="mb-2">Per-mode breakdown:</p>
          <div className="overflow-x-auto -mx-1">
            <table className="w-full text-xs">
              <thead className="text-muted-foreground">
                <tr>
                  <th className="text-left font-medium py-1 pr-3">Mode</th>
                  <th className="text-left font-medium py-1 pr-3">Lobby leave</th>
                  <th className="text-left font-medium py-1 pr-3">Mid abandon</th>
                  <th className="text-left font-medium py-1 pr-3">No submit</th>
                  <th className="text-left font-medium py-1 pr-3">No-vote</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/40">
                <tr>
                  <td className="py-1 pr-3">Quickplay</td>
                  <td className="py-1 pr-3">-1</td>
                  <td className="py-1 pr-3">-2</td>
                  <td className="py-1 pr-3">-3</td>
                  <td className="py-1 pr-3">-2</td>
                </tr>
                <tr>
                  <td className="py-1 pr-3">Ranked</td>
                  <td className="py-1 pr-3">-2</td>
                  <td className="py-1 pr-3">-5</td>
                  <td className="py-1 pr-3">-3</td>
                  <td className="py-1 pr-3">-3</td>
                </tr>
                <tr>
                  <td className="py-1 pr-3">Private</td>
                  <td className="py-1 pr-3">-1</td>
                  <td className="py-1 pr-3">-2</td>
                  <td className="py-1 pr-3">-2</td>
                  <td className="py-1 pr-3">-1</td>
                </tr>
                <tr>
                  <td className="py-1 pr-3">Flip</td>
                  <td className="py-1 pr-3">-1</td>
                  <td className="py-1 pr-3">-2</td>
                  <td className="py-1 pr-3">-3</td>
                  <td className="py-1 pr-3">-2</td>
                </tr>
                <tr>
                  <td className="py-1 pr-3">Daily</td>
                  <td className="py-1 pr-3">-1</td>
                  <td className="py-1 pr-3">-2</td>
                  <td className="py-1 pr-3">-3</td>
                  <td className="py-1 pr-3">-1</td>
                </tr>
                <tr>
                  <td className="py-1 pr-3">Tournament</td>
                  <td className="py-1 pr-3">-2</td>
                  <td className="py-1 pr-3">-7</td>
                  <td className="py-1 pr-3">-5</td>
                  <td className="py-1 pr-3">-3</td>
                </tr>
              </tbody>
            </table>
          </div>
          <ul className="list-disc pl-5 space-y-1 mt-3">
            <li>
              <strong>No-vote</strong> = you submitted a track but cast zero votes during the voting
              phase. Daily Challenge counts a single vote as enough to clear it; battle modes expect
              you to score every other entry.
            </li>
            <li>DMCA strike: tiered penalties starting at -5 (warning) up to -25 + uploader ban</li>
            <li>
              Vote-ring participation: -50. The tick worker scans every 15 minutes for mutual
              high-weight voting pairs within a short interval and auto-reports flagged accounts.
            </li>
          </ul>
          <em className="block pt-2">
            All values are admin-tunable via the rules editor and read live from the server.
          </em>
        </Q>
        <Q id="q-honor-first-offence" q="What's first-offence forgiveness?">
          The first abandon penalty in any rolling 30-day window is HALVED. Encourages occasional
          slips to clean up without making chronic offenders get off easy. So a first quickplay-mid
          abandon is -1 instead of -2; a first ranked-mid is -3 instead of -5.
        </Q>
        <Q id="q-honor-regen" q="How do I get honor back?">
          +1 per clean match completion (you submitted on time, didn't abandon, cast at least one
          vote), capped at 100. Plus a +5 burst every 10 consecutive clean matches in Quickplay or
          Ranked - tracked per mode, so a clean ranked streak counts on its own. The cap is the
          safety; you can't farm regen past your starting honor.
        </Q>
        <Q id="q-honor-gates" q="What does honor lock me out of?">
          <ul className="list-disc pl-5 space-y-1">
            <li>Below 70: no Tournament entry</li>
            <li>Below 50: no Ranked queue</li>
            <li>Below 30: no Private room hosting (you can still join others)</li>
            <li>Below 10: read-only - you can spectate but not create matches</li>
          </ul>
        </Q>
        <Q id="q-honor-perks" q="What do I get for being highly honoured?">
          At 90+ honor your votes weigh 1.5x (good actors shape the meta). There's no penalty for
          staying at 100 - the system rewards clean play rather than punishing loud opinions.
          Supporter does not change vote weight.
        </Q>
        <Q id="q-honor-disconnect" q="What if I genuinely lost wifi?">
          You have 120 seconds to reconnect from any disconnect. Refresh the page, switch tabs, lose
          wifi - if you're back inside the grace window, your seat is held. Miss the window and the
          abandon counts. The first-offence forgiveness is there for exactly this scenario.
        </Q>
      </Section>

      {/* ─── Voting ─────────────────────────────────────────────────────── */}
      <Section id="voting" title="Voting" blurb="How a winner is chosen.">
        <Q id="q-vote-how" q="How does voting work?">
          During a match's vote phase, every seated player scores each submission from 1 to 5 stars.
          The aggregate (with weights) determines the winner.
        </Q>
        <Q id="q-vote-audience" q="Can spectators vote?">
          Yes, on the{' '}
          <Link to="/vote" className="underline">
            /vote
          </Link>{' '}
          page. It surfaces matches currently in their vote phase and lets any signed-up visitor
          score the entries. Drop-in audience scores feed into the same aggregate as in-match votes
          - subject to the gate below.
        </Q>
        <Q id="q-vote-weight" q="Are all votes equal?">
          No, weighted only by honor. Your vote weight is your raw score (1-5) multiplied by an
          honor multiplier. The curve: below 30 honor = 0 weight (your votes don't count yet), 30-89
          = 1x, 90+ = 1.5x. Curve is admin-tunable. Supporter does NOT add weight - paying does not
          buy more vote.
        </Q>
        <Q id="q-vote-gate" q="Why isn't my vote counting?">
          Drive-by audience voters need to have completed at least 3 matches before their votes
          count. This stops fresh-account vote farms. Seated players in their OWN current match are
          exempt - your peer votes always count, even on your first match.
        </Q>
        <Q id="q-vote-self" q="Can I vote for my own submission?">
          No. Your own track is rendered with disabled stars and a "your track" badge in the vote
          screen and the showcase screen, so you cannot accidentally (or intentionally) score
          yourself. The API also drops self-votes silently as a server-side defense in case anything
          ever sneaks past the UI.
        </Q>
        <Q id="q-vote-lone-submitter" q="What if my opponent doesn't submit?">
          You win. If only one producer in a match uploads, that producer is rank 1 with whatever
          score the cast votes added up to (zero is fine - the rank stands). Submissions are always
          preserved at tally time, never deleted, so a partial match still produces a legitimate
          winner. This was tightened up after a bug where matches with zero net votes had their
          submissions silently scrubbed.
        </Q>
        <Q id="q-vote-window" q="How long is voting open?">
          Until the match transitions out of the vote phase (a few minutes, mode-dependent). The
          Daily Challenge is the exception - voting on past Dailies stays open indefinitely so the
          backlog can keep collecting scores.
        </Q>
        <Q id="q-vote-skip" q="What happens if I submit and then don't vote?">
          The match still completes - we tally whatever votes were cast, so the round isn't lost.
          But producers who submit and then ghost the vote phase take a small honor hit (-1 to -3
          depending on mode). First-offence forgiveness halves it. The goal is partial votes are
          better than none, but the room shouldn't pull the audience-vote weight on its own.
        </Q>
        <Q id="q-vote-partial" q="What's a 'partial tally'?">
          When the match enters Results, the Results screen shows an amber pill if not every seated
          producer voted on every other entry. Format: "X of Y producers cast votes (Z on every
          other entry)". Rankings reflect the votes that were cast - we never delete partial votes -
          but the badge tells you the result is computed from incomplete data. Voters who fully
          voted are unaffected; only ghost-voters take the honor hit.
        </Q>
      </Section>

      {/* ─── Achievements ───────────────────────────────────────────────── */}
      <Section
        id="achievements"
        title="Achievements"
        blurb="Things you can earn and choose to show or hide."
      >
        <Q id="q-ach-what" q="What achievements are there?">
          24 keys spanning streaks, milestones, tier promotions, mode mastery (Sample Flip remixer,
          genre 10-win mastery), and creator flair (first pack promoted, pack hits 500 plays, five
          packs in pool). The full catalogue is at <code>/achievements/catalogue</code>.
        </Q>
        <Q id="q-ach-earn" q="How do I earn them?">
          Most are awarded automatically at match end (lifetime match counts, genre mastery, tier
          promotion, remix master). Tier achievements (Silver, Gold, ..., Grandmaster) are stacked -
          reaching a higher tier auto-grants all lower ones.
        </Q>
        <Q id="q-ach-visibility" q="Who can see my achievements?">
          Public on your profile by default. When viewing your own profile you'll see a small "hide"
          link next to each badge that toggles its visibility on the public view. Hidden ones still
          count toward your record - they just don't appear to others.
        </Q>
        <Q id="q-ach-disable" q="Can admins disable achievements?">
          Yes - the admin Rules tab has an enable/disable map per key. Disabling stops new awards
          but doesn't revoke existing ones.
        </Q>
      </Section>

      {/* ─── A&R & scouting ─────────────────────────────────────────────── */}
      <Section
        id="ar"
        title="A&R & scouting"
        blurb="How labels and scouts find producers on the platform."
      >
        <Q id="q-ar-what" q="What is A&R on Prod Battle?">
          A&R ("artists and repertoire") is the label-scouting layer. Verified A&R users can pick
          standout tracks, co-sign producers, post briefs, and reach out to producers they want to
          work with. For producers it's a reason to keep submitting good work: every match is a
          chance to get noticed, not just to win LP.
        </Q>
        <Q id="q-ar-picks" q="What's an A&R pick?">
          When an A&R scores one of your tracks (1-5 stars, optionally with a note and a public
          co-sign), that's a pick. Picks drive the{' '}
          <Link to="/briefs" hash="scouted" className="underline">
            Most Scouted
          </Link>{' '}
          ranking and surface an "A&R's Choice" badge on the track in the feed. The more distinct
          scouts pick your work, the higher you climb.
        </Q>
        <Q id="q-ar-most-scouted" q="What is Most Scouted?">
          A leaderboard of producers ranked by A&R interest - how many different scouts picked or
          co-signed their tracks. It's a registered-user surface on the{' '}
          <Link to="/briefs" hash="scouted" className="underline">
            A&R page
          </Link>
          .
        </Q>
        <Q id="q-ar-briefs" q="What are A&R briefs?">
          A brief is a label posting "make me a beat like this." Browse open briefs at{' '}
          <Link to="/briefs" className="underline">
            /briefs
          </Link>
          , enter with a track, and the posting A&R picks a winner - who gets heard first and
          scouted. Briefs are a registered-user feature.
        </Q>
        <Q id="q-ar-contact" q="An A&R reached out to me. What does that mean?">
          A&R can send a contact request to a producer they're watching. It lands in your{' '}
          <Link to="/inbox" className="underline">
            inbox
          </Link>{' '}
          - you choose whether to accept and open a conversation. Nothing is shared until you say
          yes. You control whether you're open to A&R contact in{' '}
          <Link to="/settings" className="underline">
            /settings
          </Link>
          .
        </Q>
        <Q id="q-ar-become" q="Can I become an A&R?">
          You can apply from the A&R dashboard. Applications are reviewed by an admin; approved
          accounts get the A&R role and the picking / briefing / contact tools. The whole A&R layer
          (picks, scouting, briefs, contact, applying) is for registered users only - guests don't
          see it.
        </Q>
      </Section>

      {/* ─── Friends & inbox ────────────────────────────────────────────── */}
      <Section
        id="social"
        title="Friends & inbox"
        blurb="Connections, requests, and your message inbox."
      >
        <Q id="q-social-add-friend" q="How do I add a friend?">
          Open any producer's profile and hit "Add friend". It sends a request - the other producer
          has to accept before you're connected. Nothing happens to their account until they
          approve.
        </Q>
        <Q id="q-social-inbox" q="What's in my inbox?">
          Your{' '}
          <Link to="/inbox" className="underline">
            /inbox
          </Link>{' '}
          is one place for incoming friend requests and A&R contact requests. Accept, decline, or
          remove a connection from there. It's a registered-user surface.
        </Q>
        <Q id="q-social-approve" q="Do I have to approve friend requests?">
          Yes - connections are mutual. A request stays pending until the other party accepts;
          declining leaves no connection and doesn't notify them beyond the dismissal. You can
          remove an accepted friend at any time.
        </Q>
        <Q id="q-social-show-friends" q="Can I show or hide my friends on my profile?">
          Yes. By default your accepted friends show on your public profile. Turn the "Friends"
          visibility off in{' '}
          <Link to="/settings" className="underline">
            /settings
          </Link>{' '}
          to hide the section from other people - you still see it on your own page.
        </Q>
      </Section>

      {/* ─── Profile ────────────────────────────────────────────────────── */}
      <Section id="profile" title="Profile" blurb="Your page on the platform.">
        <Q id="q-profile-shows" q="What's on my profile?">
          Handle, avatar, bio, social links (SoundCloud, Spotify, Instagram, Twitter, YouTube,
          Bandcamp), the date you joined, your honor stars (0-5), per-genre Ranked tier with LP and
          W-L, stats grid (matches / wins / win rate / streak), recent submissions, and your
          accepted friends. Anyone can view it at <code>/@yourhandle</code>.
        </Q>
        <Q id="q-profile-edit" q="How do I edit my profile?">
          Visit your own profile page (the @handle link in the header) and click "Edit profile". You
          can change your bio, avatar, and social links.
        </Q>
        <Q id="q-profile-handle" q="Can I change my handle?">
          You pick your handle once at signup. Changing it later requires support - email{' '}
          support@prodbattle.com with the requested new handle. Squatted or impersonating handles
          can be released to the rightful owner on request with proof of identity.
        </Q>
        <Q id="q-profile-calibrating" q="My profile says 'Calibrating' instead of a tier.">
          That's the first 10 ranked matches. Tier and LP are hidden until your rating settles. Play
          a few more ranked games and the tier badge appears.
        </Q>
        <Q id="q-profile-privacy" q="Can I hide parts of my profile?">
          Yes - go to{' '}
          <Link to="/settings" className="underline">
            /settings
          </Link>{' '}
          and toggle section visibility (match history, stats, packs, achievements, friends).
          Changes apply to anyone viewing your public profile; you always see everything on your own
          page.
        </Q>
      </Section>

      {/* ─── Notifications ──────────────────────────────────────────────── */}
      <Section
        id="notifications"
        title="Notifications"
        blurb="Choose which emails you get - or none at all."
      >
        <Q id="q-notif-control" q="Can I control which emails Prod Battle sends me?">
          Yes. In{' '}
          <Link to="/settings" className="underline">
            /settings
          </Link>{' '}
          you can opt in or out of each email category independently: tournament activity, daily
          challenge activity, match results, honor alerts, A&R interest (picks and contact
          requests), and social (friend requests and accepts). Turn off the ones you don't want.
        </Q>
        <Q id="q-notif-always-on" q="Are there emails I can't turn off?">
          Only the essential ones: account security (sign-up verification, password reset) and
          billing. Everything else is optional and respects your toggles. If a preference is ever
          missing it defaults to on, so a gap never silently mutes the always-on mail.
        </Q>
        <Q id="q-notif-inbox-vs-email" q="What's the difference between my inbox and email?">
          The in-app{' '}
          <Link to="/inbox" className="underline">
            inbox
          </Link>{' '}
          always shows friend and A&R requests so you can act on them. Email is just an optional
          heads-up - turning off the "social" or "A&R interest" email categories stops the messages
          landing in your mailbox, but the requests still appear in your inbox.
        </Q>
      </Section>

      {/* ─── Genres ─────────────────────────────────────────────────────── */}
      <Section id="genres" title="Genres" blurb="System-curated and community-proposed.">
        <Q id="q-genre-system-vs-user" q="What's the difference between system and user genres?">
          System genres ship with the platform (Hardstyle, Phonk, etc.) and are always available.
          User genres are proposed by signed-up players. Each goes through a 7-day voting window;
          when the window closes the server auto-promotes any genre with at least 3 community votes
          to active status (usable in match creation by everyone) and archives the rest.
        </Q>
        <Q id="q-genre-propose" q="Can I propose a new genre?">
          Yes. Hit{' '}
          <Link to="/genres/propose" className="underline">
            /genres/propose
          </Link>
          , pick a name and the stem types matches will need (kick / snare / hihat / etc.). The URL
          slug is auto-generated from the name - emoji and special characters are stripped. Voting
          opens for 7 days.
        </Q>
        <Q id="q-genre-quota" q="Are there limits on proposals?">
          Yes - a per-week proposal quota applies, enforced server-side, to stop spam.
        </Q>
        <Q id="q-genre-vote" q="How do I vote for a proposed genre?">
          Browse{' '}
          <Link to="/genres" className="underline">
            /genres
          </Link>{' '}
          and click the vote button on any genre still in its voting window. You can retract your
          vote until the window closes.
        </Q>
        <Q id="q-genre-stems" q="What stem types should I pick when proposing?">
          The stems a kit in this genre would actually have. Hardstyle = kick, snare, fx, screech.
          UK Garage = kick, hihat, vocal, bass. Generators and uploaders use this list to build
          matching packs.
        </Q>
      </Section>

      {/* ─── Supporter ──────────────────────────────────────────────────── */}
      <Section
        id="premium"
        title="Supporter (€2,95/month)"
        blurb="Recognition + cosmetics. Zero competitive advantage."
      >
        <Q id="q-premium-perks" q="What do I get as a Supporter?">
          <ul className="list-disc pl-5 space-y-1">
            <li>★ Supporter badge next to your @handle everywhere it shows</li>
            <li>Custom profile accent color + animated avatar ring</li>
            <li>Pin up to 3 of your tracks at the top of your profile</li>
            <li>Download your own submissions as audio files</li>
            <li>10 active uploaded packs (free: 1) and 100 MB stems per pack (free: 25 MB)</li>
            <li>Discord "Supporter" role auto-assigned in the official server (coming soon)</li>
            <li>Monthly curated playlist of top tracks (Supporter-only listening)</li>
            <li>Daily Challenge access</li>
            <li>Submissions kept indefinitely (free auto-prunes at 30 days)</li>
          </ul>
        </Q>
        <Q id="q-premium-required" q="Do I need to subscribe to be competitive?">
          No - explicitly. Supporter doesn't add vote weight, doesn't pick your sample pack, doesn't
          skip the queue, doesn't buy honor. The competitive game (Quickplay, Ranked, Private rooms,
          Practice, Sample Flip, Tournaments, voting, profiles, pack uploads, achievements, honor,
          tier-related rewards) is fully playable on free forever.
        </Q>
        <Q id="q-premium-cancel" q="Can I cancel?">
          Yes, any time. You keep access until the end of the billed period, then drop back to free.
          Your accumulated rating, tier, honor, achievements, and uploaded packs are unaffected - if
          you had more than 1 active uploaded pack at cancellation, the older ones are kept on your
          account but can't be picked for new private rooms until you re-subscribe or remove them
          down to the free quota.
        </Q>
      </Section>

      {/* ─── Trust & safety ─────────────────────────────────────────────── */}
      <Section id="safety" title="Trust & safety" blurb="Reporting, copyright, conduct.">
        <Q id="q-report" q="How do I report something?">
          Hit the "Report" button on a public profile (submission cards and pack pages get the same
          button). Pick a reason (copyright, NSFW, harassment, hate, spam, impersonation, low
          quality, other) and add optional notes. We don't share the reporter identity with the
          reported user. Admins drain the queue at <code>/admin?tab=reports</code>.
        </Q>
        <Q id="q-dmca" q="How do I file a copyright takedown?">
          Email support@prodbattle.com with the standard DMCA notice fields: identification of the
          work, your contact info, a good-faith statement, and a sworn declaration. We take down on
          receipt and notify the uploader. The uploader can file a counter-notice from{' '}
          <Link to="/settings" className="underline">
            /settings
          </Link>{' '}
          (the report appears in their "Open issues" section with a counter-notice form). Confirmed
          strikes cost the uploader honor (tiered: warning, then escalating penalties up to a
          permanent uploader ban).
        </Q>
        <Q id="q-conduct" q="What gets you banned?">
          Repeat copyright infringement, sexually explicit submissions or profile content, hate
          speech, harassment, account-sharing for boosting, and chargeback fraud are grounds for
          temporary or permanent suspension. Honor-gate locks (below 50 = no ranked, below 10 =
          read-only) handle most low-grade behaviour automatically; bans are reserved for the
          serious stuff. Appeals via support@prodbattle.com.
        </Q>
        <Q id="q-data" q="Can I delete my account?">
          Yes - go to{' '}
          <Link to="/settings" className="underline">
            /settings
          </Link>{' '}
          and use the "Delete account" section. There's a 14-day grace window: logging in any time
          before the grace ends restores the account automatically. After 14 days the account is
          hard-deleted by a sweep cron - your handle is freed, your submissions are removed from the
          public catalogue, and your match history is anonymised so opponents' records are preserved
          without exposing your identity.
        </Q>
      </Section>

      {/* ─── Help ───────────────────────────────────────────────────────── */}
      <Section id="help" title="Troubleshooting" blurb="Things that occasionally go wrong.">
        <Q id="q-help-disconnect" q="I lost connection mid-match. What happens?">
          You have 120 seconds to reconnect from any disconnect (lobby or in-match). The match clock
          keeps running - that's the cost of leaving - but your seat and any in-progress submission
          are held. Refresh and you're back where you were. Miss the window and the tick worker
          marks you abandoned, with a small honor penalty (halved if it's your first slip in the
          rolling 30-day window).
        </Q>
        <Q id="q-help-upload" q="My upload failed. What do I do?">
          Check file size (20 MB cap), format (WAV / MP3 / OGG), and that you submitted before the
          deadline. If everything's in spec, retry once - if it still fails, screenshot the error
          and email support@prodbattle.com.
        </Q>
        <Q id="q-help-stuck-lobby" q="I'm stuck in a lobby that won't start.">
          Refresh the page - the room should rejoin you in the current state. If it still won't
          progress, the match may be waiting on minimum players (2 for Quickplay); private-room
          hosts can manually start once everyone is ready.
        </Q>
        <Q id="q-help-quality" q="My audio plays back at low quality.">
          We use a compressed transcode for in-browser playback so streaming stays fast. Voters
          listen to the same transcode (fairness). The original is downloadable on the match results
          page.
        </Q>
        <Q id="q-help-not-counted" q="I won a match but my rating didn't change much.">
          The K-factor is 24 after calibration (48 during your first 10 ranked matches). LP gain
          also scales with the rating gap - beating someone close to your level moves you less. A
          per-rank LP clamp prevents huge swings against very different opponents. Plus, top rank
          players have a higher cap so they can still climb.
        </Q>
        <Q id="q-help-honor-low" q="My honor dropped and I can't queue Ranked.">
          Honor below 50 locks you out of Ranked; below 70 locks Tournament. Play clean Quickplay
          matches to recover (+1 per clean match, capped at 100). The cap on regen is the safety,
          not a punishment - chronic abandoners stay locked, occasional slips recover quickly thanks
          to first-offence forgiveness.
        </Q>
        <Q id="q-help-contact" q="None of the above. How do I reach a human?">
          support@prodbattle.com. Include your handle, the match ID if relevant, and a screenshot.
        </Q>
      </Section>

      {/* ─── Roadmap ────────────────────────────────────────────────────── */}
      <Section
        id="roadmap"
        title="Roadmap"
        blurb="Everything from the original roadmap has shipped. What's left is polish that comes after observing real usage."
      >
        <Q id="q-roadmap-tournament-prizes" q="Tournament prize plumbing">
          Scheduling, registration, brackets, and auto-pairing all work end to end. Prize structures
          (entry-fee pools, fixed prize tiers, sponsored tournaments with branded packs) come next.
        </Q>
        <Q id="q-roadmap-feedback-loop" q="Continuous tuning of honor / vote / matchmaking signals">
          All thresholds and curves are admin-tunable via the rules editor. As real-world signal
          volume grows we'll revisit values: honor regen rates, vote-ring detection thresholds,
          IP-cluster sensitivity. This is operations, not engineering.
        </Q>
      </Section>

      <footer className="border-t border-border pt-6 text-sm text-muted-foreground">
        Question we missed? Email{' '}
        <a className="underline" href="mailto:support@prodbattle.com">
          support@prodbattle.com
        </a>{' '}
        and we'll add it. Or browse{' '}
        <Link to="/play" className="underline">
          /play
        </Link>{' '}
        and learn by doing.
      </footer>
    </div>
  );
}
